/**
 * TopShelf Footer
 *  - Live "OPEN NOW · Closes 7:00 PM" status using customer local time
 *    Hours are read from data-store-hours JSON on the .topshelf-footer__status element.
 *    Format: { "mon": [11,19], "tue": [11,19], ..., "sun": [11,19] }  (24h, [open, close])
 *  - Back-to-top button.
 *  - Reef Command live-chat trigger for Gorgias.
 *  - Attentive (email + SMS) dual opt-in signup. The form posts form-urlencoded
 *    in `no-cors` mode so it works without ACAO headers on the backend; we show
 *    optimistic success once the request resolves and rely on client-side
 *    validation to catch most invalid inputs before sending.
 */
(function initTopshelfFooter() {
  if (window.__topshelfFooterBound) return;
  window.__topshelfFooterBound = true;

  function dayKey(d) {
    return ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][d.getDay()];
  }

  function fmtTime(hour24, minute) {
    const d = new Date();
    d.setHours(hour24, minute || 0, 0, 0);
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
  }

  function updateStatus(el) {
    let hours;
    try { hours = JSON.parse(el.dataset.storeHours || '{}'); } catch (e) { return; }
    const now = new Date();
    const today = hours[dayKey(now)];
    const labelEl = el.querySelector('.topshelf-footer__status-label');
    const subEl   = el.querySelector('.topshelf-footer__status-sub');
    if (!labelEl || !subEl) return;

    if (!today || today.length !== 2) {
      el.dataset.open = 'false';
      labelEl.textContent = 'Closed Today';
      subEl.textContent = '';
      return;
    }

    const [openH, closeH] = today;
    const minutes = now.getHours() * 60 + now.getMinutes();
    const openMin  = openH * 60;
    const closeMin = closeH * 60;

    if (minutes >= openMin && minutes < closeMin) {
      el.dataset.open = 'true';
      labelEl.textContent = 'Open Now';
      subEl.textContent = '· Closes ' + fmtTime(closeH);
    } else if (minutes < openMin) {
      el.dataset.open = 'false';
      labelEl.textContent = 'Closed';
      subEl.textContent = '· Opens ' + fmtTime(openH);
    } else {
      el.dataset.open = 'false';
      labelEl.textContent = 'Closed';
      // Look up tomorrow's open time
      const next = new Date(now); next.setDate(now.getDate() + 1);
      const tomorrow = hours[dayKey(next)];
      if (tomorrow && tomorrow.length === 2) {
        subEl.textContent = '· Opens ' + fmtTime(tomorrow[0]) + ' tomorrow';
      } else {
        subEl.textContent = '';
      }
    }
  }

  function initStatus() {
    document.querySelectorAll('.topshelf-footer__status[data-store-hours]').forEach(updateStatus);
  }

  function initBackToTop() {
    document.querySelectorAll('.topshelf-footer__top').forEach(btn => {
      if (btn.dataset.bound) return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    });
  }

  function openGorgiasChat() {
    const chat = window.GorgiasChat;
    if (!chat) return false;

    if (typeof chat.open === 'function') {
      chat.open();
      return true;
    }

    if (typeof chat.openChat === 'function') {
      chat.openChat();
      return true;
    }

    return false;
  }

  function initGorgiasChatLinks() {
    document.querySelectorAll('[data-topshelf-gorgias-chat]').forEach(link => {
      if (link.dataset.bound) return;
      link.dataset.bound = '1';

      link.addEventListener('click', event => {
        event.preventDefault();

        if (openGorgiasChat()) return;

        let attempts = 0;
        const fallbackHref = link.getAttribute('href');
        const retry = window.setInterval(() => {
          attempts += 1;

          if (openGorgiasChat()) {
            window.clearInterval(retry);
            return;
          }

          if (attempts >= 20) {
            window.clearInterval(retry);
            if (fallbackHref) window.location.href = fallbackHref;
          }
        }, 100);
      });
    });
  }

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  function normalizePhoneUS(raw) {
    const digits = String(raw || '').replace(/\D+/g, '');
    if (digits.length === 10) return '+1' + digits;
    if (digits.length === 11 && digits.charAt(0) === '1') return '+' + digits;
    return null;
  }

  function setStatus(form, state, message) {
    const el = form.querySelector('.topshelf-footer__signup-status');
    if (!el) return;
    if (!state) {
      el.hidden = true;
      el.removeAttribute('data-state');
      el.textContent = '';
      return;
    }
    el.hidden = false;
    el.setAttribute('data-state', state);
    el.textContent = message;
  }

  function markInvalid(input, invalid) {
    if (!input) return;
    if (invalid) input.setAttribute('aria-invalid', 'true');
    else input.removeAttribute('aria-invalid');
  }

  async function submitAttentive(form) {
    const endpoint = form.dataset.endpoint || form.getAttribute('action');
    if (!endpoint) return;

    const emailInput = form.querySelector('input[name="email"]');
    const phoneInput = form.querySelector('input[name="phone"]');
    const email = (emailInput && emailInput.value || '').trim();
    const phoneRaw = (phoneInput && phoneInput.value || '').trim();

    markInvalid(emailInput, false);
    markInvalid(phoneInput, false);

    if (!EMAIL_RE.test(email)) {
      markInvalid(emailInput, true);
      setStatus(form, 'error', 'Please enter a valid email address.');
      if (emailInput) emailInput.focus();
      return;
    }

    const phoneE164 = normalizePhoneUS(phoneRaw);
    if (!phoneE164) {
      markInvalid(phoneInput, true);
      setStatus(form, 'error', 'Please enter a valid 10-digit US phone number.');
      if (phoneInput) phoneInput.focus();
      return;
    }

    setStatus(form, null);
    form.classList.add('is-submitting');

    const body = new URLSearchParams();
    body.set('email', email);
    body.set('phone', phoneE164);

    try {
      await fetch(endpoint, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
        body: body.toString(),
        credentials: 'omit',
        keepalive: true,
      });

      form.classList.remove('is-submitting');
      form.classList.add('is-success');
      setStatus(form, 'success', "You're in. Watch your inbox and texts for reef drops & VIP perks.");
      if (emailInput) emailInput.value = '';
      if (phoneInput) phoneInput.value = '';
    } catch (err) {
      form.classList.remove('is-submitting');
      setStatus(form, 'error', "Something went wrong submitting your signup. Please try again in a moment.");
    }
  }

  function initAttentiveSignup() {
    document.querySelectorAll('form[data-topshelf-attentive]').forEach(form => {
      if (form.dataset.bound) return;
      form.dataset.bound = '1';
      form.addEventListener('submit', event => {
        event.preventDefault();
        submitAttentive(form);
      });
    });
  }

  function init() {
    initStatus();
    initBackToTop();
    initGorgiasChatLinks();
    initAttentiveSignup();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Refresh open/closed badge every minute so transitions happen live.
  setInterval(initStatus, 60_000);

  document.addEventListener('shopify:section:load', init);
})();
